export interface LaunchpadLink {
  name: string;
  url: string;
  sortIndex: number;
}

export interface LaunchpadAttributes {
  name: string;
  description: string;
  logoURL?: string | null;
  campusID?: string | null;
  categoryID?: string | null;
  links: LaunchpadLink[];
}

export interface LaunchpadMetadata {
  churchID: string;
  launchpadID: string;
  attributes: LaunchpadAttributes;
}

export interface Launchpad {
  churchID: string;
  launchpadID: string;
  metadata: LaunchpadMetadata;
}

function normalizeLinks(links?: LaunchpadLink[] | null): LaunchpadLink[] {
  return [...(links ?? [])].sort((one, two) => one.sortIndex - two.sortIndex);
}

export function normalizeLaunchpadAttributes(attributes: LaunchpadAttributes): LaunchpadAttributes {
  return {
    ...attributes,
    logoURL: attributes.logoURL ?? null,
    campusID: attributes.campusID ?? null,
    categoryID: attributes.categoryID ?? null,
    links: normalizeLinks(attributes.links)
  };
}

export function normalizeLaunchpad(launchpad: Launchpad): Launchpad {
  return {
    ...launchpad,
    metadata: {
      ...launchpad.metadata,
      attributes: normalizeLaunchpadAttributes(launchpad.metadata.attributes)
    }
  };
}

export function launchpadFromMetadata(metadata: LaunchpadMetadata): Launchpad {
  return normalizeLaunchpad({
    churchID: metadata.churchID,
    launchpadID: metadata.launchpadID,
    metadata
  });
}

// Launchpad endpoints aren't consistent about how they wrap a single Launchpad:
// it may arrive under a `result` envelope or not, and as either the wrapped
// Launchpad or bare metadata. Returns null when the payload isn't a Launchpad.
export function launchpadFromResponse(body: unknown): Launchpad | null {
  if (!body || typeof body !== 'object') {
    return null;
  }

  const payload = body as Record<string, any>;
  const candidate = payload.result ?? payload.launchpad ?? payload;

  if (!candidate || typeof candidate !== 'object') {
    return null;
  }

  if (candidate.metadata?.attributes) {
    return normalizeLaunchpad(candidate as Launchpad);
  }

  if (candidate.attributes) {
    return launchpadFromMetadata(candidate as LaunchpadMetadata);
  }

  return null;
}
